/**
 * Olive hub — external tool config (Phase X.5.1).
 *
 * This module is the Era-A in-code CONFIG FILL for the hub's external
 * tiles. It is the ONE place tenant-specific tool data lives (URL +
 * display label + subheading); the tile registry in `hubTiles.ts` stays
 * tenant-neutral and refers to entries here by `hrefConfigKey`. That
 * boundary keeps Peace literals out of the product-code registry per the
 * "no tenant-specific data in product code" discipline (CLAUDE.md §3).
 *
 * Era-B swap path (documented, not built): replace `HUB_TOOL_CONFIG`
 * with a per-tenant load from `tenants.config` (ADR-007) or a dedicated
 * `tenant_tool_registry` table. The `hubTiles.ts` registry + the
 * `resolveTile` helper surface do not change; only the source of the
 * config rows moves from this const to the DB. This const then becomes
 * the Peace seed / default. See the X.5.1 design plan "Forward-watch".
 *
 * Internal tiles (Resource Directory, AI Tools) are NOT here — they are
 * app features routed by `/t/{slug}/...`, tenant-neutral, and carry their
 * (neutral) labels directly in the registry.
 */

export type HubToolKey =
  | 'sp_care_managers'
  | 'sp_spice'
  | 'sp_office'
  | 'sp_prospects'
  | 'sp_executive'
  | 'ramp'
  | 'rippling'
  | 'bill'
  | 'quickbooks'
  | 'chase'
  | 'faxage'
  | 'amazon_business'
  | 'uber_health'
  | 'mailchimp'
  | 'canva'
  | 'attio'
  | 'github'
  | 'azure';

export interface HubToolConfig {
  /** Absolute external URL the tile opens in a new tab. */
  url: string;
  /** Display label for the tile. */
  label: string;
  /** One-line subheading under the label. */
  subheading: string;
}

/**
 * Era-A Peace config fill. Tenant-specific by design (this is the seam
 * Era B externalizes to tenant config). The accompanying tile structure,
 * gating, and icons live tenant-neutrally in `hubTiles.ts`.
 */
export const HUB_TOOL_CONFIG: Record<HubToolKey, HubToolConfig> = {
  sp_care_managers: {
    url: 'https://peaceofmindcare.sharepoint.com/sites/caremanagers/',
    label: 'Care Managers (SharePoint)',
    subheading: 'Care Management Documents.',
  },
  sp_spice: {
    url: 'https://peaceofmindcare.sharepoint.com/sites/Spice',
    label: 'SPICE (SharePoint)',
    subheading: 'SPICE program documents.',
  },
  sp_office: {
    url: 'https://peaceofmindcare.sharepoint.com/sites/PeaceOffice',
    label: 'Peace Office (SharePoint)',
    subheading: 'Internal ops files.',
  },
  sp_prospects: {
    // Folder share link (operator-delivered) into the PeaceOffice site's
    // Intake/Prospects folder; deliberately the :f: share URL, not a bare
    // site URL, so the tile lands directly on the folder.
    url: 'https://peaceofmindcare.sharepoint.com/:f:/s/PeaceOffice/IgCLo3d_J9LZQ6DuQDelHzxNAcTdQaT7OtRk8Bz7CKaTe30?e=8pTsj4',
    label: 'Prospects (SharePoint)',
    subheading: 'Intake and prospect files.',
  },
  sp_executive: {
    url: 'https://peaceofmindcare.sharepoint.com/sites/executive',
    label: 'Peace Executive (SharePoint)',
    subheading: 'Leadership-only documents.',
  },
  ramp: {
    url: 'https://app.ramp.com/sign-in',
    label: 'Ramp',
    subheading: 'Cards and expense management.',
  },
  rippling: {
    url: 'https://app.rippling.com/dashboard',
    label: 'Rippling',
    subheading: 'HR, payroll, and benefits.',
  },
  bill: {
    url: 'https://login.us.bill.com/neo/login',
    label: 'Bill.com',
    subheading: 'Accounts payable and invoices.',
  },
  quickbooks: {
    url: 'https://qbo.intuit.com/app/homepage',
    label: 'QuickBooks Online',
    subheading: 'Bookkeeping and the ledger.',
  },
  chase: {
    url: 'https://secure.chase.com/web/auth/dashboard#/dashboard/overview',
    label: 'Chase',
    subheading: 'Business banking.',
  },
  faxage: {
    url: 'https://www.faxage.com/',
    label: 'Faxage',
    subheading: 'Send and receive faxes.',
  },
  amazon_business: {
    url: 'https://business.amazon.com/',
    label: 'Amazon Business',
    subheading: 'Business supplies and purchasing.',
  },
  uber_health: {
    url: 'https://www.uberhealth.com',
    label: 'Uber Health',
    subheading: 'Rides for clients. Not consumer Uber.',
  },
  mailchimp: {
    url: 'https://login.mailchimp.com/',
    label: 'Mailchimp',
    subheading: 'Email campaigns and newsletters.',
  },
  canva: {
    url: 'https://www.canva.com/',
    label: 'Canva',
    subheading: 'Flyers, decks, and social posts.',
  },
  attio: {
    url: 'https://app.attio.com',
    label: 'Attio',
    subheading: 'Referral partners and outside relationships.',
  },
  github: {
    url: 'https://github.com/peace-care-network/peace-os',
    label: 'GitHub',
    subheading: 'The codebase, pull requests, and issues.',
  },
  azure: {
    url: 'https://portal.azure.com',
    label: 'Azure Portal',
    subheading: 'Cloud infrastructure and hosting.',
  },
};

/**
 * URL-only view of the config (the `hrefConfigKey` map per the committed
 * decision). Kept as a derived export for any consumer that needs the URL
 * alone without the label/subheading.
 */
export const HUB_TOOL_URLS: Record<HubToolKey, string> = Object.fromEntries(
  Object.entries(HUB_TOOL_CONFIG).map(([key, cfg]) => [key, cfg.url]),
) as Record<HubToolKey, string>;
